import type { PendingRecordingMeta } from "./pending-recording";
import type { KeyValueStorage } from "./storage";

// Corbeille des prises ABANDONNÉES (incident 2026-09-04 : 2 h 07 détruites par un
// tap sur « Annuler »). Le natif garde les fichiers 7 jours dans Trash/<id>/ ;
// ce module garde, côté app, ce que le natif ignore : la prise elle-même (type,
// titre, projet, contexte) pour la restaurer telle quelle, et l'état « vu » de
// la notice. Pur : zéro dépendance Capacitor.

export const TRASH_META_KEY = "captureBesoins.trashMeta";
export const TRASH_CHANGED_EVENT = "ecoute:trash-changed";
export const TRASH_RETENTION_DAYS = 7;
// En dessous, rien de significatif à protéger : abandonner sans demander.
export const CANCEL_CONFIRM_MIN_MS = 3_000;

export interface TrashMeta {
  trashId: string;
  trashedAt: string;
  durationMs: number;
  take: PendingRecordingMeta | null;
  seenAt: string | null;
}

export interface TrashNativeEntry {
  id: string;
  trashedAt: string;
  durationMs: number;
  segmentCount: number;
}

export interface TrashRow extends TrashNativeEntry {
  meta: TrashMeta | null;
}

export interface TrashMetaStore {
  list(): Promise<TrashMeta[]>;
  remember(entry: TrashMeta): Promise<void>;
  forget(trashId: string): Promise<void>;
  unseen(): Promise<TrashMeta[]>;
  markAllSeen(nowIso: string): Promise<void>;
  reconcile(nativeIds: string[]): Promise<TrashMeta[]>;
}

/** Sous ce seuil, une durée garde ses secondes : arrondie à la minute, 88 s
 *  s'affichait « 1 min » — faux de près de moitié (essais L3, 2026-09-28). */
export const DURATION_SECONDS_BELOW_MS = 180_000;

// Espace insécable (OQLF) : entre le nombre et son unité, et entre les groupes,
// pour qu'une durée ne se coupe jamais en fin de ligne.
const NBSP = "\u00A0";
const unite = (n: number, u: string) => `${n}${NBSP}${u}`;

export function formatTrashDuration(ms: number, options: { secondes?: boolean } = {}): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (total < 60) return unite(total, total <= 1 ? "seconde" : "secondes");
  // « 1 h 29 min », jamais « 1 h 29 » : c'est la notation d'une heure de la
  // journée, qui se confond avec « 13 h 42 » (relecture Regard, 2026-09-25).
  // Symboles h, min, s sans point ni pluriel, jamais mêlés à un mot en toutes
  // lettres (OQLF : « 2 h 28 min », « 10 min 35 s »). Arrondi vers le bas : une
  // durée conservée n'est jamais gonflée.
  const avecSecondes =
    s > 0 && (options.secondes === true || total * 1000 < DURATION_SECONDS_BELOW_MS);
  const groupes: string[] = [];
  if (h > 0) groupes.push(unite(h, "h"));
  if (m > 0 || (h > 0 && avecSecondes)) groupes.push(unite(m, "min"));
  if (avecSecondes) groupes.push(unite(s, "s"));
  return groupes.join(NBSP);
}

export function cancelConfirmMessage(durationMs: number): string | null {
  if (durationMs < CANCEL_CONFIRM_MIN_MS) return null;
  return (
    `Abandonner cet enregistrement (${formatTrashDuration(durationMs)}) ?\n\n` +
    `Il restera ${TRASH_RETENTION_DAYS} jours dans la corbeille de l'appareil, d'où tu pourras le restaurer.`
  );
}

/**
 * Phrase de la notice d'accueil après une mise à la corbeille (abandon, ou
 * « Mettre à la corbeille » sur un enregistrement retrouvé) — source unique,
 * lue par `TrashNotice`.
 */
export function trashNoticeMessage(entries: Pick<TrashMeta, "durationMs">[]): string {
  if (entries.length > 1) {
    return `${entries.length} enregistrements ont été mis à la corbeille. Ils y restent ${TRASH_RETENTION_DAYS} jours.`;
  }
  return `Un enregistrement de ${formatTrashDuration(entries[0]?.durationMs ?? 0)} a été mis à la corbeille. Il y reste ${TRASH_RETENTION_DAYS} jours.`;
}

export function trashEntryLabel(meta: TrashMeta | null): string {
  const take = meta?.take;
  if (!take) return "Enregistrement";
  const type = take.type === "rencontre" ? "Rencontre" : "Idée flash";
  return take.title ? `${type} — ${take.title}` : type;
}

export function mergeTrashRows(native: TrashNativeEntry[], metas: TrashMeta[]): TrashRow[] {
  const byId = new Map(metas.map((m) => [m.trashId, m]));
  return [...native]
    .sort((a, b) => (a.trashedAt < b.trashedAt ? 1 : a.trashedAt > b.trashedAt ? -1 : 0))
    .map((n) => ({ ...n, meta: byId.get(n.id) ?? null }));
}

function parseMetas(raw: string | null): TrashMeta[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TrashMeta[]) : [];
  } catch {
    return [];
  }
}

export function createTrashMetaStore(kv: KeyValueStorage): TrashMetaStore {
  const read = async () => parseMetas(await kv.get(TRASH_META_KEY));
  let queue: Promise<unknown> = Promise.resolve();
  function mutate(f: (list: TrashMeta[]) => TrashMeta[]): Promise<TrashMeta[]> {
    const next = queue.then(async () => {
      const list = f(await read());
      if (list.length === 0) await kv.remove(TRASH_META_KEY);
      else await kv.set(TRASH_META_KEY, JSON.stringify(list));
      return list;
    });
    queue = next.catch(() => {});
    return next;
  }
  return {
    list: read,
    remember: async (entry) => {
      await mutate((l) => [...l.filter((m) => m.trashId !== entry.trashId), entry]);
    },
    forget: async (trashId) => {
      await mutate((l) => l.filter((m) => m.trashId !== trashId));
    },
    unseen: async () => (await read()).filter((m) => m.seenAt === null),
    markAllSeen: async (nowIso) => {
      await mutate((l) => l.map((m) => (m.seenAt === null ? { ...m, seenAt: nowIso } : m)));
    },
    reconcile: (nativeIds) => {
      const keep = new Set(nativeIds);
      return mutate((l) => l.filter((m) => keep.has(m.trashId)));
    },
  };
}
